export const getEndUserTokenKeyByHandler = (handler) => `${handler}_endUserToken`;

const hasValue = (value) => {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim().length > 0;
    return true;
};

/** Own-property lookup: a config key such as `toString` must miss these maps, not inherit from Object.prototype. */
const ownValue = (map, key) => (Object.prototype.hasOwnProperty.call(map, key) ? map[key] : undefined);

export const SELF_FIELD_MAP = {
    fname:        (eu) => hasValue(eu?.fname),
    lname:        (eu) => hasValue(eu?.lname),
    phone:        (eu) => hasValue(eu?.phone),
    imageUrl:     (eu) => hasValue(eu?.imageUrl),
    birth_date:   (eu) => hasValue(eu?.birth_date),
    idOrPassport: (eu) => hasValue(eu?.idOrPassport),
    social_link:  (eu) => hasValue(eu?.social_link),
};

/**
 * Required-field key -> the label the app already shows for that field on its own profile forms
 * (`family.*`). Deliberately NOT a new set of scanner-only strings: the guard reads out the field
 * name, and the family then opens CompleteFamilyProfileDialog and looks for that exact same words
 * -- "ת״ז / דרכון" at the gate, "ת״ז / דרכון" on the form. A parallel wording would be two names
 * for one field.
 */
export const PROFILE_FIELD_LABEL_KEYS = {
    fname: 'family.first_name',
    lname: 'family.last_name',
    imageUrl: 'family.photo',
    birth_date: 'family.date_of_birth',
    phone: 'family.phone',
    idOrPassport: 'family.id_or_passport',
    social_link: 'family.social_link',
};

/**
 * Turn the backend's `missingFields` keys into readable labels.
 *
 * The list is OPEN: required fields are per-seller config, so a key can arrive that this app has
 * no label for (`dynamicFields.membershipNo`, or a field added to a template after this build).
 * An unknown key is rendered rather than dropped -- a guard who reads out "membershipNo" can still
 * get the family to the right box, whereas a silently shortened list sends them away thinking they
 * are done. Unknown keys are wrapped in Unicode isolates so a Latin key inside a Hebrew sentence
 * cannot drag the surrounding punctuation to the wrong end of the line.
 */
export function missingFieldLabels(missingFields, translate) {
    if (!Array.isArray(missingFields)) return [];
    return missingFields
        .filter((field) => typeof field === 'string' && field.trim())
        .map((field) => {
            const labelKey = ownValue(PROFILE_FIELD_LABEL_KEYS, field);
            if (labelKey) return translate(labelKey);
            const raw = field.startsWith('dynamicFields.') ? field.slice('dynamicFields.'.length) : field;
            return `\u2068${raw}\u2069`;
        });
}

export function isSelfComplete(endUser, requiredFields) {
    if (!Array.isArray(requiredFields) || requiredFields.length === 0) return true;
    return requiredFields.every((field) => {
        if (typeof field !== 'string') return false;
        if (field.startsWith('dynamicFields.')) {
            const key = field.slice('dynamicFields.'.length);
            // Own-property here too: dynamicFields is buyer-shaped data, so a required
            // `dynamicFields.toString` would otherwise be satisfied by Object.prototype.
            return hasValue(ownValue(endUser?.dynamicFields ?? {}, key));
        }
        return ownValue(SELF_FIELD_MAP, field)?.(endUser) ?? false;
    });
}

/**
 * An explicit non-active status is a DIFFERENT refusal from an unfinished profile, and it
 * outranks it: a cancelled membership does not get in however complete the profile is. A missing
 * status reads as active -- only the backend's own word for it counts, so a response that omits
 * the field cannot label a live member "inactive".
 */
export const isMemberInactive = (member) => !!member?.status && member.status !== 'active';

/**
 * Why this member cannot be checked in, as one line for the gate.
 *
 * Status first. A member who is both cancelled and unfinished used to be told only what was
 * missing, so the family went off to add a photo and came back to the same refusal -- the real
 * blocker was never on screen.
 */
export function memberBlockReason(member, translate) {
    if (isMemberInactive(member)) return translate('scanner.member_inactive');
    if (!member?.isProfileComplete) {
        const fields = missingFieldLabels(member?.missingFields, translate);
        return fields.length > 0
            ? translate('scanner.member_missing', { fields: fields.join(', ') })
            : translate('scanner.member_incomplete');
    }
    return translate('scanner.member_inactive');
}

/**
 * Fields a member may stop seeing on their personal-area form (#1512). Only `social_link` for now:
 * it is optional config that many sellers never tick, so showing it to a member whose plans do not
 * ask for it is just noise.
 *
 * CRM `dynamicFields.*` keys follow the same rule and do not need listing here: a seller's custom
 * fields ("Additional Information") are configured per account, with free-text names this app
 * cannot know, so they are matched by their prefix instead. Whatever the seller has not ticked on
 * a plan the member holds is noise to that member too -- JBZ's business name and number are the
 * case that asked for it.
 */
export const HIDEABLE_PROFILE_FIELDS = ['social_link'];

/** Prefix of a CRM custom field's key, as the plan's required list spells it. */
const DYNAMIC_FIELD_PREFIX = 'dynamicFields.';

/** A named CRM custom field, not the bare prefix. */
const isDynamicFieldKey = (field) =>
    typeof field === 'string'
    && field.startsWith(DYNAMIC_FIELD_PREFIX)
    && field.length > DYNAMIC_FIELD_PREFIX.length;

/**
 * Details that describe who the member IS. Once filled, the member cannot change them themselves
 * (#1512): a swapped ID number or birth date would let someone else's details ride on an existing
 * membership, and gender joins them because Ori's rule is that everything but the email address
 * settles once entered. The photo is the fourth such detail, but its "filled" test differs (a
 * stock cartoon is not a real photo), so it has its own helper, `isPhotoLocked`.
 *
 * Email is deliberately absent: it is the one datum the member is meant to keep current.
 *
 * None of these can be a plan's required field except `idOrPassport` and `birth_date`
 * (SeasonTicketDialog's allowed-values set), so widening this list cannot strand anyone at the
 * activation gate. An EMPTY value is never locked regardless -- see `isIdentityFieldLocked`.
 */
export const LOCKED_IDENTITY_FIELDS = ['idOrPassport', 'birth_date', 'gender'];

/**
 * Every field key that at least one of the member's own memberships requires.
 *
 * This mirrors the split the shipped surfaces already use (EndUserHeader, IncompleteProfilesChecklist,
 * PricingPlanCard): a family row reads `familyAdultRequiredFields`, any other row reads
 * `seasonTicketRequiredFields`. The ADULT list is the right one here because only an adult has a
 * login to this form. The result is a union because the gate checks each plan on its own, so a
 * field ticked by any one plan has to stay visible and fillable.
 */
export function heldRequiredFields(subscriptions) {
    if (!Array.isArray(subscriptions)) return [];
    const held = new Set();
    subscriptions.forEach((subscription) => {
        const list = subscription?.familyRole
            ? subscription.familyAdultRequiredFields
            : subscription?.seasonTicketRequiredFields;
        if (!Array.isArray(list)) return;
        list.forEach((field) => {
            if (typeof field === 'string' && field.trim()) held.add(field);
        });
    });
    return [...held];
}

/**
 * Whether the form should drop this field because none of the member's plans asks for it.
 *
 * Fails open in every doubtful case: a field outside HIDEABLE_PROFILE_FIELDS is never hidden, and
 * neither is anything while `subscriptions` is not a loaded array or is empty. A member with no
 * membership (or whose memberships are still loading) has no plan to judge against, and hiding a
 * field a plan turns out to require would leave them unable to fill it.
 */
export function isProfileFieldHidden(field, subscriptions) {
    if (!HIDEABLE_PROFILE_FIELDS.includes(field) && !isDynamicFieldKey(field)) return false;
    if (!Array.isArray(subscriptions) || subscriptions.length === 0) return false;
    return !heldRequiredFields(subscriptions).includes(field);
}

/**
 * Whether the member may no longer edit this identity field. Read from the STORED value passed in,
 * never from live form state: typing into an empty field must not lock it under the member's
 * fingers. An EMPTY field is never locked -- a plan may require it, and a locked empty field would
 * mean refusal at the gate with no way to fix it.
 */
export function isIdentityFieldLocked(field, storedValues) {
    if (!LOCKED_IDENTITY_FIELDS.includes(field)) return false;
    return hasValue(storedValues?.[field]);
}

/**
 * Whether the member's photo is locked: they uploaded one. A stock cartoon is display-only and
 * nobody chose it, so it stays replaceable. The cartoon test is inlined (it is the twin of
 * `isPlaceholderAvatar` in src/utils/userAvatar.js:28) because importing that module pulls in
 * market.js and ~20 image assets, and this file sits in the scanner store's module graph.
 */
export function isPhotoLocked(imageUrl) {
    return hasValue(imageUrl) && !(typeof imageUrl === 'string' && imageUrl.includes('/assets/images/avatars/'));
}
